import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Popup } from "react-map-gl/mapbox";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { DateTime } from "luxon";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { rain, DataSourceInfo } from "@/endpoints/rain/rain";
import { TIME_PERIODS, type TimePeriodsValue } from "@/config/date";
import { getDateRangeFromTimePeriod } from "@/components/TimePeriod/helper";
import { POPUP_ANIM, RainContent, type RainContentData } from "./RainContent";

const DAILY_AGGREGATE_PERIODS: TimePeriodsValue[] = ["last90d", "last1y", "last10y"];
const FALLBACK_PERIOD: TimePeriodsValue = "last30d";

// The rain API rejects ISO timestamps with a non-UTC offset (e.g. "+02:00")
// with a 400 — match the original page-map behaviour and always emit UTC `Z`.
const timePeriodToISO = (period: TimePeriodsValue): { start: string; end: string } => {
	const range = getDateRangeFromTimePeriod(period) ?? getDateRangeFromTimePeriod(FALLBACK_PERIOD);
	if (!range?.from || !range.to) {
		const now = DateTime.utc().toISO() ?? "";
		return { start: now, end: now };
	}
	return {
		start: DateTime.fromJSDate(range.from).toUTC().toISO() ?? "",
		end: DateTime.fromJSDate(range.to).toUTC().toISO() ?? "",
	};
};

interface RainPopupProps {
	lng: number;
	lat: number;
	timePeriod: TimePeriodsValue;
	onClose: () => void;
	onSourceInfoChange?: (sourceInfo: DataSourceInfo | null) => void;
}

export const RainPopup: React.FC<RainPopupProps> = observer(({ lng, lat, timePeriod, onClose, onSourceInfoChange }) => {
	const timeseriesApi = useAsyncStateStore(rain.getTimeseries);
	const statisticsApi = useAsyncStateStore(rain.getStatistics);
	const isDailyView = DAILY_AGGREGATE_PERIODS.includes(timePeriod);

	useEffect(() => {
		const isoRange = timePeriodToISO(timePeriod);
		if (isDailyView) {
			statisticsApi.fetch({
				lon: lng,
				lat,
				...isoRange,
				forceSource: "radar",
				rollingWindowMinutes: 1440,
				rollingAggregation: "sum",
			});
		} else {
			timeseriesApi.fetch({
				lon: lng,
				lat,
				...isoRange,
				samples: 200,
				forceSource: "radar",
				gapFill: true,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lng, lat, timePeriod]);

	const activeApi = isDailyView ? statisticsApi : timeseriesApi;
	const sourceInfo = activeApi.data?.sourceInfo ?? null;

	useEffect(() => {
		onSourceInfoChange?.(sourceInfo);
	}, [sourceInfo, onSourceInfoChange]);

	const data: RainContentData | null = isDailyView
		? statisticsApi.data
			? {
					mode: "daily",
					rollingWindows: statisticsApi.data.rollingWindows ?? [],
					aggregates: statisticsApi.data.aggregates ?? null,
					timePeriodLabel: TIME_PERIODS.find(period => period.value === timePeriod)?.label,
				}
			: null
		: timeseriesApi.data
			? {
					mode: "raw",
					timeseries: timeseriesApi.data.data ?? [],
					timePeriodLabel: TIME_PERIODS.find(period => period.value === timePeriod)?.label,
				}
			: null;

	return (
		<Popup
			className="popup-rain"
			longitude={lng}
			latitude={lat}
			closeButton={false}
			closeOnClick={false}
			closeOnMove={false}
			anchor="bottom"
			maxWidth="340px"
			onClose={onClose}
		>
			<motion.div className="min-w-[300px]" variants={POPUP_ANIM} initial="initial" animate="animate" exit="exit">
				<div className="flex items-start justify-between gap-3 px-3.5 pt-3 pb-2">
					<div className="min-w-0">
						<h3 className="text-sm font-semibold text-foreground leading-tight truncate">Precipitation</h3>
						<div className="flex items-center gap-1.5 mt-1">
							<span className="px-1 py-0 text-[8px] leading-4 font-medium uppercase tracking-wide rounded bg-cyan-50 text-cyan-700 border border-cyan-200 dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-800">
								Radar
							</span>
							<span className="text-[10px] text-muted-foreground">
								{lat.toFixed(4)}, {lng.toFixed(4)}
							</span>
						</div>
					</div>
					<button
						className="p-1 rounded-md hover:bg-muted transition-colors flex-shrink-0 -mr-1 -mt-0.5"
						onClick={onClose}
					>
						<X className="h-3.5 w-3.5 text-muted-foreground" />
					</button>
				</div>
				<div className="border-t border-border mx-3.5" />
				<div className="px-3.5 pt-2.5 pb-3">
					<RainContent loading={activeApi.loading} data={data} />
				</div>
			</motion.div>
		</Popup>
	);
});
